// Простые функции работы с backend UML
// baseUrl настраивается через REACT_APP_API_BASE_URL
import { gzip, ungzip } from 'pako';
import { z } from 'zod';
import exampleDiagram from '../example.json';
const baseUrl = process.env.REACT_APP_API_BASE_URL || '';

const elementSchema = z.object({
    id: z.any(),
    type: z.enum(['rectangle','circle','arrow','text']),
    x: z.number(),
    y: z.number(),
    x2: z.number().optional(),
    y2: z.number().optional(),
    width: z.number().optional(),
    height: z.number().optional(),
    radius: z.number().optional(),
    content: z.string().optional(),
    fontSize: z.number().optional(),
    fontFamily: z.string().optional(),
    fill: z.string().optional(),
    stroke: z.string().optional(),
    strokeWidth: z.number().optional(),
    dashed: z.boolean().optional(),
    startCap: z.string().optional(),
    endCap: z.string().optional(),
    arrowSize: z.number().optional(),
    pathPoints: z.array(z.object({ x: z.number(), y: z.number() })).optional(),
    startConnection: z.object({ elementId: z.any(), side: z.string() }).nullable().optional(),
    endConnection: z.object({ elementId: z.any(), side: z.string() }).nullable().optional(),
    attachedToElement: z.any().optional(),
    attachedToArrow: z.any().optional(),
    z: z.number().optional(),
    isNote: z.boolean().optional(), // добавлено
    isDatabase: z.boolean().optional() // добавлено
});

const diagramSchema = z.object({
    meta: z.object({
        name: z.string(),
        version: z.number().int().min(1),
        createdAt: z.string().datetime().optional(),
        updatedAt: z.string().datetime(),
        domain: z.enum(['Architecture Vision','Business Architecture','Information Systems Architecture','Technology Architecture']).optional()
    }),
    view: z.object({ zoom: z.number(), scroll: z.object({ left: z.number(), top: z.number() }) }),
    elements: z.array(elementSchema)
});

function validateDiagram(payload){
    return diagramSchema.parse(payload);
}

function encodePayload(payload){
    const json = JSON.stringify(payload);
    if (json.length > 8000) { // порог компрессии ~8KB
        const compressed = gzip(json);
        return { body: compressed, headers: { 'Content-Type':'application/json', 'Content-Encoding':'gzip' } };
    }
    return { body: json, headers: { 'Content-Type':'application/json' } };
}

async function decodeIfCompressed(res){
    const enc = res.headers.get('Content-Encoding');
    if (enc === 'gzip') {
        const arr = new Uint8Array(await res.arrayBuffer());
        const txt = new TextDecoder().decode(ungzip(arr));
        return JSON.parse(txt);
    }
    return res.json();
}

const useMock = !baseUrl; // если не задан backend, используем мок

// Простая in-memory база для моков
const mockStore = new Map();
const mockVersions = new Map(); // id -> [{version, updatedAt, meta, view, elements}]
const EXAMPLE_ID = 'example';
if (!mockStore.has(EXAMPLE_ID)) {
    try {
        const clone = JSON.parse(JSON.stringify(exampleDiagram));
        // Гарантируем обязательные поля
        clone.meta = clone.meta || { name: 'Пример', version: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
        clone.meta.createdAt = clone.meta.createdAt || clone.meta.updatedAt;
        clone.meta.domain = clone.meta.domain || 'Architecture Vision';
        mockStore.set(EXAMPLE_ID, clone);
        mockVersions.set(EXAMPLE_ID, [{ version: clone.meta.version || 1, updatedAt: clone.meta.updatedAt, ...clone }]);
    } catch (e) { /* ignore */ }
}

function mockDelay(result, ms=200){
    return new Promise((resolve)=> setTimeout(()=> resolve(result), ms));
}
function mockError(message, ms=200){
    return new Promise((_,reject)=> setTimeout(()=> reject(new Error(message)), ms));
}

function nextVersion(id){
    const versions = mockVersions.get(id) || [];
    return (versions.length ? Math.max(...versions.map(v=>v.version)) : 0) + 1;
}

export async function saveUmlDiagram(payload) {
    if (useMock) {
        const data = validateDiagram(payload);
        const id = 'd' + Date.now();
        data.meta.version = data.meta.version || 1;
        data.meta.createdAt = data.meta.createdAt || new Date().toISOString();
        data.meta.updatedAt = new Date().toISOString();
        data.meta.domain = data.meta.domain || 'Architecture Vision';
        mockStore.set(id, JSON.parse(JSON.stringify(data)));
        mockVersions.set(id, [{ version: data.meta.version, updatedAt: data.meta.updatedAt, ...JSON.parse(JSON.stringify(data)) }]);
        return mockDelay({ id, ...data });
    }
    validateDiagram(payload);
    const enc = encodePayload(payload);
    const res = await fetch(`${baseUrl}/diagrams`, {
        method: 'POST',
        headers: { ...enc.headers },
        body: enc.body
    });
    if (!res.ok) {
        const text = await res.text();
        throw new Error(`Ошибка сохранения (${res.status}): ${text}`);
    }
    const doc = await decodeIfCompressed(res);
    return { id: doc.doc_id, meta: doc.meta, view: doc.view, elements: doc.elements };
}

export async function updateUmlDiagram(id, payload) {
    if (useMock) {
        if (!mockStore.has(id)) return mockError('Диаграмма не найдена');
        const data = validateDiagram(payload);
        const current = mockStore.get(id);
        const newVer = nextVersion(id);
        data.meta.version = newVer;
        data.meta.createdAt = current.meta.createdAt || data.meta.createdAt || new Date().toISOString();
        data.meta.updatedAt = new Date().toISOString();
        data.meta.domain = data.meta.domain || current.meta.domain || 'Architecture Vision';
        mockStore.set(id, JSON.parse(JSON.stringify(data)));
        const versions = mockVersions.get(id) || [];
        versions.push({ version: newVer, updatedAt: data.meta.updatedAt, ...JSON.parse(JSON.stringify(data)) });
        mockVersions.set(id, versions);
        return mockDelay({ id, ...data });
    }
    validateDiagram(payload);
    const enc = encodePayload(payload);
    const res = await fetch(`${baseUrl}/diagrams/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { ...enc.headers },
        body: enc.body
    });
    if (!res.ok) {
        const text = await res.text();
        throw new Error(`Ошибка обновления (${res.status}): ${text}`);
    }
    const doc = await decodeIfCompressed(res);
    return { id: doc.doc_id, meta: doc.meta, view: doc.view, elements: doc.elements };
}

export async function loadUmlDiagram(id) {
    if (useMock) {
        if (!id) return mockError('ID не указан');
        let versionReq = null;
        const qIdx = id.indexOf('?');
        if (qIdx !== -1) { // поддержка ?version=X
            const pure = id.substring(0,qIdx);
            const params = new URLSearchParams(id.substring(qIdx+1));
            const ver = params.get('version');
            if (ver) versionReq = parseInt(ver,10);
            id = pure;
        }
        if (!mockStore.has(id)) return mockError('Не найдено');
        if (versionReq) {
            const versions = mockVersions.get(id)||[];
            const found = versions.find(v=> v.version === versionReq);
            if (!found) return mockError('Версия не найдена');
            const copy = JSON.parse(JSON.stringify(found));
            delete copy.id; // структура как у сохранённой
            return mockDelay(copy);
        }
        const data = mockStore.get(id);
        return mockDelay(JSON.parse(JSON.stringify(data)));
    }
    const res = await fetch(`${baseUrl}/diagrams/${encodeURIComponent(id)}`);
    if (!res.ok) {
        const text = await res.text();
        throw new Error(`Ошибка загрузки (${res.status}): ${text}`);
    }
    const doc = await decodeIfCompressed(res);
    return { meta: doc.meta, view: doc.view, elements: doc.elements };
}

export async function listUmlDiagrams() {
    if (useMock) {
        const list = Array.from(mockStore.entries()).map(([id, d]) => ({ id, meta: d.meta }));
        // сортируем по updatedAt desc
        list.sort((a,b)=> new Date(b.meta?.updatedAt||0) - new Date(a.meta?.updatedAt||0));
        return mockDelay(list);
    }
    const res = await fetch(`${baseUrl}/diagrams/list`);
    if (!res.ok) {
        const text = await res.text();
        throw new Error(`Ошибка списка (${res.status}): ${text}`);
    }
    const summaries = await decodeIfCompressed(res);
    return summaries.map(s => ({ id: s.doc_id, meta: s.meta }));
}

export async function listDiagramVersions(id){
    if (useMock) {
        if (!mockVersions.has(id)) return mockDelay([]);
        const versions = mockVersions.get(id).map(v => ({ version: v.version, updatedAt: v.updatedAt }));
        versions.sort((a,b)=> b.version - a.version);
        return mockDelay(versions);
    }
    return [];
}

// Пример структуры сохраняемого JSON
export const UML_DIAGRAM_SAMPLE = {
    meta: {
        name: 'Новая диаграмма',
        version: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    },
    view: {
        zoom: 1,
        scroll: { left: 0, top: 0 }
    },
    elements: [
        // Прямоугольник
        {
            id: 123,
            type: 'rectangle',
            x: 100, y: 120, width: 160, height: 70,
            fill: '#ffffff', stroke: '#000000', strokeWidth: 2,
            z: 101
        },
        // Текст
        {
            id: 124,
            type: 'text',
            x: 180, y: 155,
            content: 'Сервис', fontSize: 16, fontFamily: 'Arial', fill: '#000000',
            attachedToElement: 123,
            z: 300
        },
        // Ломаная стрелка
        {
            id: 125,
            type: 'arrow',
            x: 260, y: 155, x2: 420, y2: 210,
            stroke: '#000000', strokeWidth: 2, startCap: 'none', endCap: 'arrow',
            dashed: false,
            pathPoints: [
                { x: 260, y: 155 },
                { x: 320, y: 155 },
                { x: 320, y: 210 },
                { x: 420, y: 210 }
            ],
            startConnection: { elementId: 123, side: 'right' },
            endConnection: null,
            z: 200
        }
    ]
};

export const isMockBackend = useMock;
